<script lang="ts">
	import type { Device } from 'src/types/Device';
	import { format } from 'src/services/i18n';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { getContext, onDestroy, onMount } from 'svelte';
	import type { Writable } from 'svelte/store';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';
	import { Button, Select, Text } from '@Shelly/shelly-design-system';
	import CameraRtspSettings from './CameraRtsp/CameraRtspSettings.svelte';
	import {
		getCameraCapabilities,
		getCameraConfig,
		saveCameraConfig,
	} from '../../helpers/camera-settings-helpers';
	import { hasCameraConfigKey } from '../../helpers/camera-features';
	import type { CameraStreamConfig } from '../../types/camera-settings';

	export let device: Device;

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	const supportsRtsp = hasCameraConfigKey(device, 'rtsp');

	const cloneStreams = (
		src: Record<string, CameraStreamConfig>,
	): Record<string, CameraStreamConfig> => {
		const out: Record<string, CameraStreamConfig> = {};
		for (const [k, v] of Object.entries(src)) {
			out[k] = { ...v };
		}
		return out;
	};

	const readStreams = (): Record<string, CameraStreamConfig> => {
		const raw = getCameraConfig(device)?.streams;
		if (!raw || typeof raw !== 'object') return {};
		const out: Record<string, CameraStreamConfig> = {};
		for (const [k, v] of Object.entries(raw as Record<string, any>).sort(
			([a], [b]) => Number(a) - Number(b),
		)) {
			out[k] = {
				bitrate: typeof v?.bitrate === 'number' ? v.bitrate : 512,
				resolution: typeof v?.resolution === 'string' ? v.resolution : '640x360@10',
			};
		}
		return out;
	};

	let streams = readStreams();
	let cache = cloneStreams(streams);
	let loading = false;
	let resolutionOptions = Object.values(streams).map((s) => ({
		title: s.resolution,
		value: s.resolution,
	}));
	let bitrateOptions = Object.values(streams).map((s) => ({
		title: $format('camera_stream_bitrate_value', {
			default: '{bitrate} kbps',
			values: { bitrate: s.bitrate },
		}),
		value: s.bitrate,
	}));

	$: streamKeys = Object.keys(streams).sort((a, b) => Number(a) - Number(b));

	$: mainResolution = streams['0']?.resolution ?? '';

	$: hasChanges = streamKeys.some(
		(k) =>
			streams[k]?.resolution !== cache[k]?.resolution || streams[k]?.bitrate !== cache[k]?.bitrate,
	);

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			stickyBottomAction.set({
				onClick: save,
				loading,
				disabled: !hasChanges || loading,
				secondaryButton: {
					onClick: resetToDefaults,
					label: $format('button_reset_to_defaults', { default: 'Reset to defaults' }),
					variant: 'secondary',
				},
			});
		} else {
			stickyBottomAction.set(null);
		}
	}

	const getSelectValue = (e: CustomEvent): any => {
		const d = e.detail;
		if (typeof d === 'string' || typeof d === 'number') return d;
		return d?.value ?? d;
	};

	const streamLabel = (key: string): string => {
		return key === '0'
			? $format('camera_stream_main', { default: 'Main stream' })
			: $format('camera_substream_n', { values: { n: key }, default: `Substream #${key}` });
	};

	/** `1280x720@25` ranked by pixel count, with the frame rate breaking ties. */
	const rankResolution = (value: string): number[] => {
		const [size = '', fps = ''] = String(value).split('@');
		const [width, height] = size.split('x').map(Number);
		return [(width || 0) * (height || 0), Number(fps) || 0];
	};

	const isBelow = (value: string, ceiling: string): boolean => {
		const [pixels, fps] = rankResolution(value);
		const [ceilingPixels, ceilingFps] = rankResolution(ceiling);
		return pixels === ceilingPixels ? fps < ceilingFps : pixels < ceilingPixels;
	};

	/**
	 * Substreams must stay below the main stream. Matching it is only offered when the
	 * hardware exposes nothing smaller.
	 */
	const allowedResolutions = (key: string, options: typeof resolutionOptions, ceiling: string) => {
		if (key === '0' || !ceiling) return options;
		const below = options.filter((option) => isBelow(String(option.value), ceiling));
		return below.length ? below : options.filter((option) => option.value === ceiling);
	};

	/** Pulls substreams back under the ceiling after the main stream is lowered. */
	const clampSubstreams = () => {
		for (const key of streamKeys) {
			const allowed = allowedResolutions(key, resolutionOptions, mainResolution);
			if (!allowed.length || allowed.some((o) => o.value === streams[key]?.resolution)) continue;
			const best = allowed.reduce((top, option) =>
				isBelow(String(top.value), String(option.value)) ? option : top,
			);
			streams[key] = { ...streams[key], resolution: String(best.value) };
		}
		streams = streams;
	};

	const save = async () => {
		if (!hasChanges) return;
		loading = true;
		const success = await saveCameraConfig(
			device,
			{ streams: cloneStreams(streams) },
			$format('setting_scc_saved_toast', { default: 'Setting saved successfully.' }),
		);
		if (success) {
			cache = cloneStreams(streams);
		} else {
			streams = cloneStreams(cache);
		}
		loading = false;
	};

	const resetToDefaults = async () => {
		loading = true;
		const success = await saveCameraConfig(
			device,
			{ streams: null },
			$format('camera_streams_reset_success', { default: 'Streams reset to defaults.' }),
		);
		if (success) {
			streams = readStreams();
			cache = cloneStreams(streams);
		}
		loading = false;
	};

	onMount(async () => {
		const caps = await getCameraCapabilities(device);
		if (caps) {
			resolutionOptions = caps.resolutions.map((r) => ({ title: r, value: r }));
			const [min, max] = caps.bitrate_range;
			const step = min || 512;
			const steps: number[] = [];
			for (let b = min; b <= max; b += step) steps.push(b);
			if (steps[steps.length - 1] !== max) steps.push(max);
			bitrateOptions = steps.map((b) => ({
				title: $format('camera_stream_bitrate_value', {
					default: '{bitrate} kbps',
					values: { bitrate: b },
				}),
				value: b,
			}));
			clampSubstreams();
			cache = cloneStreams(streams);
		}
	});

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});
</script>

{#each streamKeys as key (key)}
	{@const options = allowedResolutions(key, resolutionOptions, mainResolution)}
	<div class="mb-m">
		<Text styleType="h2" class="mb-s">
			{streamLabel(key)}
		</Text>
		<Select
			size="m"
			width="full"
			icon="fas fa-display"
			label={$format('camera_stream_resolution', { default: 'Resolution' })}
			{options}
			value={options.find((o) => o.value === streams[key]?.resolution)}
			on:change={(e) => {
				streams[key] = { ...streams[key], resolution: getSelectValue(e) };
				streams = streams;
				if (key === '0') clampSubstreams();
			}}
			id="camera-stream-res-{key}"
			aria-label={$format('camera_stream_resolution', { default: 'Resolution' })}
		/>
		<Select
			size="m"
			width="full"
			class="mt-s"
			icon="fas fa-gauge-high"
			label={$format('camera_stream_bitrate', { default: 'Bitrate' })}
			options={bitrateOptions}
			value={bitrateOptions.find((o) => o.value === streams[key]?.bitrate)}
			on:change={(e) => {
				streams[key] = { ...streams[key], bitrate: getSelectValue(e) };
				streams = streams;
			}}
			id="camera-stream-bitrate-{key}"
			aria-label={$format('camera_stream_bitrate', { default: 'Bitrate' })}
		/>
	</div>
{/each}

{#if supportsRtsp}
	<CameraRtspSettings {device} />
{/if}

{#if !$showNewLayout}
	<Button
		class="mb-s"
		variant="secondary"
		width="full"
		size="m"
		disabled={loading}
		on:click={resetToDefaults}
	>
		{$format('button_reset_to_defaults', { default: 'Reset to defaults' })}
	</Button>
	<Button width="full" size="m" {loading} disabled={!hasChanges || loading} on:click={save}>
		{$format('button_save', { default: 'Save' })}
	</Button>
{/if}
